import { useMemo } from 'react';

import { LicenseCoarseLevels, UserAccessBadge } from '../../../../hooks/useAccessBadge';
import { GeneratorCoarseLicenseLevel } from '../../../../services/TemplateService/Template.type';
import { notUndefined } from '../../../../utils/general';
import { getConfig } from '../../../../utils/store';
import { ConsumerGenerator } from './consumer-generator';
import { TestprepPlanIcon } from './TestPrepTab';
import { TestPrepVariantMap as tmap } from './TestPrepVariantMap';
interface QuestionType {
  id: string; // e.g. 'SCR', 'ECR', 'text_input', etc.
  displayName: string;
  grades: (string | number)[]; // All valid grades
  lessonVariants: LessonVariant[];
}
export interface DropdownOption {
  displayName: string;
  value: string;
  disabled?: boolean;
  displayDivider?: boolean;
  icon?: JSX.Element;
  isEnterprise?: boolean;
}

export interface Test {
  id: string; // e.g. 'STAAR_RLA'
  displayName: string; // e.g. 'RLA'
  testGroup: DropdownOption; // e.g. 'STAAR' or 'TELPAS'
  states: DropdownOption[]; // e.g. ['TX'], ['TX','CA'], etc.
  questionTypes: QuestionType[];
}

export type LessonVariant = {
  variant: string;
  displayName?: string;
  lessonId: string;
  isPremium?: boolean;
};

const config = getConfig();

export function useTestPrepTree(): Test[] {
  return templatedTests;
}

const _templatedTests = [
  // CALIFORNIA CAASPP
  {
    id: 'CAASPP',
    displayName: 'ELA',
    testGroup: { value: 'CAASPP', displayName: 'CAASPP' },
    states: [{ displayName: 'California', value: 'CA' }],
    questionTypes: [
      {
        id: 'SA',
        displayName: 'Short Answer',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: false,
            variant: 'informational',
            displayName: 'Short Answer',
            lessonId:
              tmap['Short Constructed Response'].variants['CA CAASPP ELA Short Answer'],
          },
          {
            isPremium: true,
            variant: 'upload',
            displayName: 'Upload',
            lessonId:
              tmap['Use My Materials - Short Constructed Response'].variants[
                'CA CAASPP ELA Short Answer Curify'
              ],
          },
        ],
      },
      {
        id: 'PP',
        displayName: 'Paired Passage Short Answer',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'paired-passage',
            displayName: 'Paired Passage Short Answer',
            lessonId:
              tmap['Paired Passage Short Constructed Response'].variants[
                'CA CAASPP ELA Short Answer Paired Passages'
              ],
          },
          {
            isPremium: true,
            variant: 'paired-passage-upload',
            displayName: 'Paired Passage Short Answer',
            lessonId:
              tmap['Use My Materials - Paired Passage Short Constructed Response']
                .variants['CA CAASPP ELA Short Answer Paired Passage Curity'],
          },
        ],
      },
      {
        id: 'PT - Informational',
        displayName: 'Performance Task - Informational',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'informational-composition',
            displayName: 'Informational Composition',
            lessonId:
              tmap['Writing Performance Task'].variants[
                'CA CAASPP ELA Informational Performance Task'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-informational-composition',
            displayName: 'Informational Composition',
            lessonId:
              tmap['Use My Materials - Writing Performance Task'].variants[
                'CA CAASPP ELA Informational Performance Task Curify'
              ],
          },
        ],
      },
      {
        id: 'PT - Opinion',
        displayName: 'Performance Task - Opinion',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'opinion-composition',
            displayName: 'Opinion Composition',
            lessonId:
              tmap['Writing Performance Task'].variants[
                'CA CAASPP ELA Opinion Performance Task'
              ],
          },

          {
            isPremium: true,
            variant: 'upload-opinion-composition',
            displayName: 'Opinion Composition',
            lessonId:
              tmap['Use My Materials - Writing Performance Task'].variants[
                'CA CAASPP ELA Opinion Performance Task Curify'
              ],
          },
        ],
      },
      {
        id: 'PT - Argumentative',
        displayName: 'Performance Task - Argumentative',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'argumentative-composition',
            displayName: 'Argumentative Composition',
            lessonId:
              tmap['Writing Performance Task'].variants[
                'CA CAASPP Argumentative Performance Task'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-argumentative-composition',
            displayName: 'Argumentative Composition',
            lessonId:
              tmap['Use My Materials - Writing Performance Task'].variants[
                'CA CAASPP Argumentative Performance Task Curify'
              ],
          },
        ],
      },
      {
        id: 'PT - Narrative',
        displayName: 'Performance Task - Narrative',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'narrative-composition',
            displayName: 'Narrative Composition',
            lessonId:
              tmap['Writing Performance Task'].variants[
                'CA CAASPP Narrative Performance Task'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-narrative-composition',
            displayName: 'Narrative Composition',
            lessonId:
              tmap['Use My Materials - Writing Performance Task'].variants[
                'CA CAASPP Narrative Performance Task Curify'
              ],
          },
        ],
      },
    ],
  },
  // California Math
  {
    id: 'CAASPP_MATH',
    displayName: 'Math',
    testGroup: { value: 'CAASPP', displayName: 'CAASPP' },
    states: [{ displayName: 'California', value: 'CA' }],
    questionTypes: [
      {
        id: '1-point-constructed-response',
        displayName: '1-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: false,
            variant: '1-point-constructed-response',
            displayName: '1-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants[
                'CA CAASPP Math 1-Point'
              ],
          },
        ],
      },
      {
        id: '2-point-constructed-response',
        displayName: '2-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '2-point-constructed-response',
            displayName: '2-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants[
                'CA CAASPP Math 2-Point'
              ],
          },
        ],
      },
      {
        id: '3-point-constructed-response',
        displayName: '3-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '3-point-constructed-response',
            displayName: '3-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants[
                'CA CAASPP Math 3-Point'
              ],
          },
        ],
      },
      {
        id: '4-point-constructed-response',
        displayName: '4-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '4-point-constructed-response',
            displayName: '4-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants['CA CAASPP 4-Point'],
          },
        ],
      },
      {
        id: '1-point-performance-task-component',
        displayName: '1-Point Performance Task Component',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '1-point-performance-task-component',
            displayName: '1-Point Performance Task Component',
            lessonId:
              tmap['Performance Task Component'].variants[
                'CA CAASPP Math Performance Task 1-Point'
              ],
          },
        ],
      },
      {
        id: '2-point-performance-task-component',
        displayName: '2-Point Performance Task Component',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '2-point-performance-task-component',
            displayName: '2-Point Performance Task Component',
            lessonId:
              tmap['Performance Task Component'].variants[
                'CA CAASPP Math Performance Task 2-Point'
              ],
          },
        ],
      },
      {
        id: '3-point-performance-task-component',
        displayName: '3-Point Performance Task Component',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '3-point-performance-task-component',
            displayName: '3-Point Performance Task Component',
            lessonId:
              tmap['Performance Task Component'].variants[
                'CA CAASPP Performance Task 3-Point'
              ],
          },
        ],
      },
      {
        id: '4-point-performance-task-component',
        displayName: '4-Point Performance Task Component',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '4-point-performance-task-component',
            displayName: '4-Point Performance Task Component',
            lessonId:
              tmap['Performance Task Component'].variants[
                'CA CAASPP Performance Task 4-Point'
              ],
          },
        ],
      },
      {
        id: 'performance-task-simulation',
        displayName: 'Performance Task Simulation',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'performance-task-simulation',
            displayName: 'Performance Task Simulation',
            lessonId:
              tmap['Math Performance Task Simulation'].variants[
                'CA CAASPP Math Performance Task'
              ],
          },
        ],
      },
    ],
  },
  // California Science
  {
    id: 'CAASPP_SCI',
    displayName: 'Science',
    testGroup: { value: 'CAASPP', displayName: 'CAASPP' },
    states: [{ displayName: 'California', value: 'CA' }],
    questionTypes: [
      {
        id: 'SCR',
        displayName: 'Short Constructed Response (SCR)',
        grades: [5, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.caasppScienceSCR,
          },
          {
            isPremium: true,
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.caasppScienceSCRUpload,
          },
        ],
      },
    ],
  },
  // California ELPAC
  {
    id: 'ELPAC',
    displayName: 'EL Proficiency',
    testGroup: { value: 'ELPAC', displayName: 'ELPAC' },
    states: [{ displayName: 'California', value: 'CA' }],
    questionTypes: [
      {
        id: 'write_about_an_experience',
        displayName: 'Write About an Experience',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.elpacWriteAboutAnExperience,
          },
        ],
      },
    ],
  },
  // TN TCAP ELA
  {
    id: 'TCAP_ELA',
    displayName: 'ELA',
    testGroup: { value: 'TCAP', displayName: 'TCAP' },
    states: [{ displayName: 'Tennessee', value: 'TN' }],
    questionTypes: [
      {
        id: 'ECR 3rd Grade Informational Response ',
        displayName: '3rd Grade Extended Response - Informational',
        grades: [3],
        lessonVariants: [
          {
            isPremium: false,
            variant: 'informational-response',
            displayName: 'Informational Response',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'TN TCAP ELA 3rd Grade Informational Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-informational-response',
            displayName: 'Informational Response',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'TN TCAP ELA 3rd Grade Informational Extended Response Curify'
              ],
          },
        ],
      },
      {
        id: 'ECR 3rd Grade Opinion Response',
        displayName: '3rd Grade Extended Response - Opinion',
        grades: [3],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'opinion-response',
            displayName: 'Opinion Response',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'TN TCAP ELA 3rd Grade Opinion Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-opinion-response',
            displayName: 'Opinion Response',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'TN TCAP ELA 3rd Grade Opinion Extended Response Curify'
              ],
          },
        ],
      },
      {
        id: 'ECR 3rd Grade Narrative Response',
        displayName: '3rd Grade Extended Response - Narrative',
        grades: [3],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'narrative-response',
            displayName: 'Narrative Response',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'TN TCAP ELA 3rd Grade Narrative Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-narrative-response',
            displayName: 'Narrative Response',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'TN TCAP ELA 3rd Grade Narrative Extended Response Curify'
              ],
          },
        ],
      },
      {
        id: 'ECR - Informational',
        displayName: 'Extended Response - Informational',
        grades: [4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: false,
            variant: 'informational-response',
            displayName: 'Informational Response',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'TN TCAP ELA Informational Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-informational-response',
            displayName: 'Informational Response',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'TN TCAP ELA Informational Extended Response Curify'
              ],
          },
        ],
      },
      {
        id: 'ECR - Argument',
        displayName: 'Extended Response - Argument',
        grades: [4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'argument-response',
            displayName: 'Argument Response',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'TN TCAP ELA Argument Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-argument-response',
            displayName: 'Argument Response',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'TN TCAP ELA Opinion Extended Response Curify'
              ],
          },
        ],
      },
      {
        id: 'ECR - Narrative',
        displayName: 'Extended Response - Narrative',
        grades: [4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'narrative-response',
            displayName: 'Narrative Response',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'TN TCAP ELA Narrative Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-narrative-response',
            displayName: 'Narrative Response',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'TN TCAP ELA Narrative Extended Response Curify'
              ],
          },
        ],
      },
      {
        id: 'Paired Passage ECR - Informational',
        displayName: 'Paired Passage Extended Response - Informational',
        grades: [4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'paired-passage-ecr-informational',
            displayName: 'Paired Passage Extended Response - Informational',
            lessonId:
              tmap['Paired Passage Extended Constructed Response'].variants[
                'TN TCAP ELA Informational Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-paired-passage-ecr-informational',
            displayName: 'Paired Passage Extended Response - Informational',
            lessonId:
              tmap['Use My Materials - Paired Passage Extended Constructed Response']
                .variants['TN TCAP ELA Informational Extended Response'],
          },
        ],
      },
      {
        id: 'Paired Passage ECR - Opinion',
        displayName: 'Paired Passage Extended Response - Opinion',
        grades: [4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'paired-passage-ecr-opinion',
            displayName: 'Paired Passage Extended Response - Opinion',
            lessonId:
              tmap['Paired Passage Extended Constructed Response'].variants[
                'TN TCAP ELA Opinion Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-paired-passage-ecr-opinion',
            displayName: 'Paired Passage Extended Response - Opinion',
            lessonId:
              tmap['Use My Materials - Paired Passage Extended Constructed Response']
                .variants['TN TCAP ELA Opinion Extended Response'],
          },
        ],
      },
      {
        id: 'Paired Passage ECR - Argument',
        displayName: 'Paired Passage Extended Response - Argument',
        grades: [4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'paired-passage-ecr-argument',
            displayName: 'Paired Passage Extended Response - Argument',
            lessonId:
              tmap['Paired Passage Extended Constructed Response'].variants[
                'TN TCAP ELA Argument Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-paired-passage-ecr-argument',
            displayName: 'Paired Passage Extended Response - Argument',
            lessonId:
              tmap['Use My Materials - Paired Passage Extended Constructed Response']
                .variants['TN TCAP ELA Argument Extended Response'],
          },
        ],
      },
      {
        id: 'Paired Passage ECR - Narrative',
        displayName: 'Paired Passage Extended Response - Narrative',
        grades: [4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'paired-passage-ecr-narrative',
            displayName: 'Paired Passage Extended Response - Narrative',
            lessonId:
              tmap['Paired Passage Extended Constructed Response'].variants[
                'TN TCAP ELA Narrative Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-paired-passage-ecr-narrative',
            displayName: 'Paired Passage Extended Response - Narrative',
            lessonId:
              tmap['Use My Materials - Paired Passage Extended Constructed Response']
                .variants['TN TCAP ELA Narrative Extended Response'],
          },
        ],
      },
    ],
  },
  // TN TCAP Math
  {
    id: 'TCAP_Math',
    displayName: 'Math',
    testGroup: { value: 'TCAP', displayName: 'TCAP' },
    states: [{ displayName: 'Tennessee', value: 'TN' }],
    questionTypes: [
      {
        id: '1-point-constructed-response',
        displayName: '1-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: false,
            variant: '1-point-constructed-response',
            displayName: '1-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants['TN TCAP Math 1-Point'],
          },
        ],
      },
    ],
  },
  // TEXAS STAAR RLA
  {
    id: 'STAAR_RLA',
    displayName: 'RLA',
    testGroup: { value: 'STAAR', displayName: 'STAAR' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: 'SCR',
        displayName: 'Short Constructed Response (SCR)',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: false,
            variant: 'short-answer',
            displayName: 'Short Answer',
            lessonId: tmap['Short Constructed Response'].variants['TX STAAR ELA SCR'],
          },
          {
            isPremium: true,
            variant: 'upload',
            displayName: 'Upload',
            lessonId:
              tmap['Use My Materials - Short Constructed Response'].variants[
                'TX STAAR ELA SCR Curify'
              ],
          },
        ],
      },
      {
        id: 'PP',
        displayName: 'Paired Passage (SCR)',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'paired-passage',
            displayName: 'Paired Passage',
            lessonId:
              tmap['Paired Passage Short Constructed Response'].variants[
                'TX STAAR ELA SCR Paired Passages'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-paired-passage',
            displayName: 'Upload',
            lessonId:
              tmap['Use My Materials - Paired Passage Short Constructed Response']
                .variants['TX STAAR ELA SCR Paired Passage Curify'],
          },
        ],
      },
      {
        id: 'revising_sentences',
        displayName: 'Revising Sentences (SCR)',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'revising-sentences',
            displayName: 'Revising Sentences',
            lessonId: config.testprep.staarRLASCRRevisingSentences,
          },
        ],
      },
      {
        id: 'ECR - Informational',
        displayName: 'Extended Constructed Response (ECR) - Informational',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'informational-composition',
            displayName: 'Informational Composition',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'TX STAAR ELA Informational ECR'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-informational-composition',
            displayName: 'Informational Composition',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'TX STAAR ELA Informational ECR Curify'
              ],
          },
        ],
      },
      {
        id: 'ECR - Argumentative',
        displayName: 'Extended Constructed Response (ECR) - Argumentative',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'argumentative-composition',
            displayName: 'Argumentative Composition',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'TX STAAR ELA Argumentative ECR'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-argumentative-composition',
            displayName: 'Argumentative Composition',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'TX STAAR ELA Argumentative ECR Curify'
              ],
          },
        ],
      },
      {
        id: 'ECR - Correspondence',
        displayName: 'Extended Constructed Response (ECR) - Correspondence',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'correspondence-composition',
            displayName: 'Correspondence Composition',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'TX STAAR ELA Correspondence ECR'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-informational-correspondence',
            displayName: 'Correspondence Composition',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'TX STAAR ELA Correspondence ECR Curify'
              ],
          },
        ],
      },
      {
        id: 'Paired Passage ECR - Informational',
        displayName: 'Paired Passage ECR - Informational',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'paired-passage-ecr-informational',
            displayName: 'Paired Passage ECR - Informational',
            lessonId:
              tmap['Paired Passage Extended Constructed Response'].variants[
                'TX STAAR ELA Informational ECR'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-informational-correspondence',
            displayName: 'Correspondence Composition',
            lessonId:
              tmap['Use My Materials - Paired Passage Extended Constructed Response']
                .variants['TX STAAR ELA Informational ECR'],
          },
        ],
      },
      {
        id: 'Paired Passage ECR - Argumentative',
        displayName: 'Paired Passage ECR - Argumentative',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'paired-passage-ecr-argumentative',
            displayName: 'Paired Passage ECR - Argumentative',
            lessonId:
              tmap['Paired Passage Extended Constructed Response'].variants[
                'TX STAAR ELA Argumentative ECR'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-informational-correspondence',
            displayName: 'Correspondence Composition',
            lessonId:
              tmap['Use My Materials - Paired Passage Extended Constructed Response']
                .variants['TX STAAR ELA Argumentative ECR'],
          },
        ],
      },
      {
        id: 'Paired Passage ECR - Correspondence',
        displayName: 'Paired Passage ECR - Correspondence',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'paired-passage-ecr-correspondence',
            displayName: 'Paired Passage ECR - Correspondence',
            lessonId:
              tmap['Paired Passage Extended Constructed Response'].variants[
                'TX STAAR ELA Correspondence ECR'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-informational-correspondence',
            displayName: 'Correspondence Composition',
            lessonId:
              tmap['Use My Materials - Paired Passage Extended Constructed Response']
                .variants['TX STAAR ELA Correspondence ECR'],
          },
        ],
      },
    ],
  },
  // TEXAS STAAR Social Studies
  {
    id: 'STAAR_SS',
    displayName: 'Social Studies',
    testGroup: { value: 'STAAR', displayName: 'STAAR' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: 'SCR',
        displayName: 'Short Constructed Response (SCR)',
        grades: [6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.staarSocialStudiesSCR,
          },
          {
            isPremium: true,
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.staarSocialStudiesSCRUpload,
          },
        ],
      },
    ],
  },
  // TEXAS STAAR Science
  {
    id: 'STAAR_SCI',
    displayName: 'Science',
    testGroup: { value: 'STAAR', displayName: 'STAAR' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: 'SCR',
        displayName: 'Short Constructed Response (SCR)',
        grades: [6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.staarScienceSCR,
          },
          {
            isPremium: true,
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.staarScienceSCRUpload,
          },
        ],
      },
    ],
  },
  // TEXAS STAAR Math
  {
    id: 'STAAR_MATH',
    displayName: 'Math',
    testGroup: { value: 'STAAR', displayName: 'STAAR' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: '1-point-constructed-response',
        displayName: 'Equation Editor/Text Entry (1 point)',
        grades: [3, 4, 5, 6, 7, 8, 9, 10],
        lessonVariants: [
          {
            isPremium: false,
            variant: '1-point-constructed-response',
            displayName: 'Equation Editor/Text Entry (1 point)',
            lessonId:
              tmap['Math Constructed Response Question'].variants[
                'TX STAAR Math 1-Point'
              ],
          },
        ],
      },
      {
        id: '2-point-constructed-response',
        displayName: 'Equation Editor/Text Entry (2 points)',
        grades: [3, 4, 5, 6, 7, 8, 9, 10],
        lessonVariants: [
          {
            isPremium: true,
            variant: '2-point-constructed-response',
            displayName: 'Equation Editor/Text Entry (2 points)',
            lessonId:
              tmap['Math Constructed Response Question'].variants[
                'TX STAAR Math 2-Pont 2-Part'
              ],
          },
        ],
      },
    ],
  },
  // TEXAS TELPAS
  {
    id: 'TELPAS',
    displayName: 'EL Proficiency',
    testGroup: { value: 'TELPAS', displayName: 'TELPAS' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: 'tell_a_story_answer_questions',
        displayName: 'Tell a Story: Answer Questions',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.telpasTellAStoryAnswerQuestions,
          },
        ],
      },
      {
        id: 'tell_a_story_four_pictures',
        displayName: 'Tell a Story: Four Pictures',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.telpasTELLAStoryFourPictures,
          },
        ],
      },
      {
        id: 'tell_a_story_one_picture',
        displayName: 'Tell a Story: One Picture',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.telpasTELLAStoryOnePicture,
          },
        ],
      },
      {
        id: 'describe_the_pictures',
        displayName: 'Describe the Pictures',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.telpasDescribeThePictures,
          },
        ],
      },
      {
        id: 'respond_to_a_scenario',
        displayName: 'Respond to a Scenario',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.telpasRespondToAScenario,
          },
        ],
      },
      {
        id: 'write_about_bar_graph',
        displayName: 'Write About Bar Graph',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.telpasWriteAboutBarGraph,
          },
        ],
      },
    ],
  },
  // PENNSYLVANIA Keystone
  {
    id: 'PA_Keystone',
    displayName: 'ELA',
    testGroup: { value: 'PA_Keystone', displayName: 'Keystone' },
    states: [{ displayName: 'Pennsylvania', value: 'PA' }],
    questionTypes: [
      {
        id: 'keystone_lit',
        displayName: 'Literature Constructed Response',
        grades: [9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: false,
            variant: 'default',
            displayName: 'Informational',
            lessonId:
              tmap['Short Constructed Response'].variants[
                'PA PSSA ELA Literature EOC Constructed Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload',
            displayName: 'Upload',
            lessonId:
              tmap['Use My Materials - Short Constructed Response'].variants[
                'PA PSSA ELA Literature EOC Constructed Response Curify'
              ],
          },
        ],
      },
    ],
  },
  // PENNSYLVANIA PSSA ELA
  {
    id: 'PSSA_ELA',
    displayName: 'ELA',
    testGroup: { value: 'PSSA', displayName: 'PSSA' },
    states: [{ displayName: 'Pennsylvania', value: 'PA' }],
    questionTypes: [
      {
        id: '3rd_grade_short_answer',
        displayName: '3rd Grade Short-Answer',
        grades: [3],
        lessonVariants: [
          {
            isPremium: false,
            variant: 'informational',
            displayName: 'Informational',
            lessonId:
              tmap['Short Constructed Response'].variants[
                'PA PSSA ELA Reading Short-Answer'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-3rd-grade-short-answer',
            displayName: 'Upload',
            lessonId:
              tmap['Use My Materials - Short Constructed Response'].variants[
                'PA PSSA ELA Reading Short-Answer Curify'
              ],
          },
        ],
      },
      {
        id: 'text_dependent_analysis',
        displayName: 'Text-Dependent Analysis',
        grades: [4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: false,
            variant: 'default',
            displayName: 'Informational',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'PA PSSA ELA Text-Dependent Analysis'
              ],
          },
          {
            isPremium: true,
            variant: 'upload',
            displayName: 'Upload',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'PA PSSA ELA Text-Dependent Analysis Curify'
              ],
          },
        ],
      },
    ],
  },
  // PA Math
  {
    id: 'PSSA_Math',
    displayName: 'Math',
    testGroup: { value: 'PSSA', displayName: 'PSSA' },
    states: [{ displayName: 'Pennsylvania', value: 'PA' }],
    questionTypes: [
      {
        id: '4-point-open-ended-question',
        displayName: '4-Point Open Ended Question',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: false,
            variant: 'default',
            displayName: '4-Point Open Ended Question',
            lessonId: config.testprep.pssaMath4PointOpenEndedQuestion,
          },
        ],
      },
    ],
  },
  // PENNSYLVANIA PSSA Science
  {
    id: 'PSSA_SCI',
    displayName: 'Science',
    testGroup: { value: 'PSSA', displayName: 'PSSA' },
    states: [{ displayName: 'Pennsylvania', value: 'PA' }],
    questionTypes: [
      {
        id: 'Short Answer',
        displayName: 'Short-Answer',
        grades: [5, 8],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.pssaScienceShortAnswer,
          },
        ],
      },
    ],
  },
  // MICHIGAN M-STEP ELA
  {
    id: 'M-STEP',
    displayName: 'ELA',
    testGroup: { value: 'M-STEP', displayName: 'M-STEP' },
    states: [{ displayName: 'Michigan', value: 'MI' }],
    questionTypes: [
      {
        id: 'passage_based_writing',
        displayName: 'Passage-Based Writing',
        grades: [3, 4, 5, 6, 7],
        lessonVariants: [
          {
            isPremium: false,
            variant: 'passage-based-writing',
            displayName: 'Passage-Based Writing',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'MI M-STEP ELA Passage-based Writing'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-passage-based-writing',
            displayName: 'Passage-Based Writing',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'MI M-STEP ELA Passage-based Writing Curify'
              ],
          },
        ],
      },
    ],
  },
  // MICHIGAN M-STEP Math
  {
    id: 'M-STEP Math',
    displayName: 'Math',
    testGroup: { value: 'M-STEP', displayName: 'M-STEP' },
    states: [{ displayName: 'Michigan', value: 'MI' }],
    questionTypes: [
      {
        id: '1-point-constructed-response',
        displayName: '1-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: false,
            variant: '1-point-constructed-response',
            displayName: '1-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants[
                'MI M-STEP Math 1-Point'
              ],
          },
        ],
      },
      {
        id: '2-point-constructed-response',
        displayName: '2-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '2-point-constructed-response',
            displayName: '2-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants[
                'MI M-STEP Math 2-Point'
              ],
          },
        ],
      },
      {
        id: '3-point-constructed-response',
        displayName: '3-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '3-point-constructed-response',
            displayName: '3-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants[
                'MI M-STEP Math 3-Point'
              ],
          },
        ],
      },
      {
        id: '4-point-constructed-response',
        displayName: '4-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '4-point-constructed-response',
            displayName: '4-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants[
                'MI M-STEP Math 4-Point'
              ],
          },
        ],
      },
    ],
  },
  // Arizona AASA ELA
  {
    id: 'Arizona AASA ELA',
    displayName: 'ELA',
    testGroup: { value: 'arizona_aasa', displayName: 'AASA' },
    states: [{ displayName: 'Arizona', value: 'AZ' }],
    questionTypes: [
      {
        id: 'short_answer',
        displayName: 'Short Answer',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: false,
            variant: 'short_answer',
            displayName: 'Short Answer',
            lessonId:
              tmap['Short Constructed Response'].variants['AZ AASA ELA Short Answer'],
          },
          {
            isPremium: true,
            variant: 'upload',
            displayName: 'Upload',
            lessonId:
              tmap['Use My Materials - Short Constructed Response'].variants[
                'AZ AASA ELA Short Answer Curify'
              ],
          },
        ],
      },
      {
        id: 'paired-passage',
        displayName: 'Paired Passage Short Answer',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'paired-passage',
            displayName: 'Paired Passage Short Answer',
            lessonId:
              tmap['Paired Passage Short Constructed Response'].variants[
                'AZ AASA ELA Short Answer Paired Passages'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-paired-passage',
            displayName: 'Paired Passage Short Answer',
            lessonId:
              tmap['Use My Materials - Paired Passage Short Constructed Response']
                .variants['AZ AASA ELA Short Answer Paired Passage Curify'],
          },
        ],
      },

      {
        id: 'extended-constructed-response-informational',
        displayName: 'Extended Response - Informative',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'informative-constructed-response',
            displayName: 'Informative Response',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'AZ AASA ELA Informative Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-informative-constructed-response',
            displayName: 'Informative Response',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'AZ AASA ELA Informative Extended Response Curify'
              ],
          },
        ],
      },
      {
        id: 'extended-constructed-response-opinion',
        displayName: 'Extended Response - Opinion',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'opinion-constructed-response',
            displayName: 'Opinion Response',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'AZ AASA ELA Opinion Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-opinion-constructed-response',
            displayName: 'Opinion Response',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'AZ AASA ELA Opinion Extended Response Curify'
              ],
          },
        ],
      },
      {
        id: 'extended-constructed-response-argumentative',
        displayName: 'Extended Response - Argumentative',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'argumentative-constructed-response',
            displayName: 'Argumentative Response',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'AZ AASA ELA Argumentative Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-argumentative-constructed-response',
            displayName: 'Argumentative Response',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'AZ AASA ELA Argumentative Extended Response Curify'
              ],
          },
        ],
      },
      {
        id: 'paired-passage-extended-response-informative',
        displayName: 'Paired Passage Extended Response - Informative',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'paired-passage-extended-response-informative',
            displayName: 'Paired Passage Extended Response - Informative',
            lessonId:
              tmap['Paired Passage Extended Constructed Response'].variants[
                'AZ AASA ELA Informative Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-paired-passage-extended-response-informative',
            displayName: 'Paired Passage Extended Response - Informative',
            lessonId:
              tmap['Use My Materials - Paired Passage Extended Constructed Response']
                .variants['AZ AASA ELA Informative Extended Response'],
          },
        ],
      },
      {
        id: 'paired-passage-extended-response-opinion',
        displayName: 'Paired Passage Extended Response - Opinion',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'paired-passage-extended-response-opinion',
            displayName: 'Paired Passage Extended Response - Opinion',
            lessonId:
              tmap['Paired Passage Extended Constructed Response'].variants[
                'AZ AASA ELA Opinion Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-paired-passage-extended-response-opinion',
            displayName: 'Paired Passage Extended Response - Opinion',
            lessonId:
              tmap['Use My Materials - Paired Passage Extended Constructed Response']
                .variants['AZ AASA ELA Opinion Extended Response'],
          },
        ],
      },
      {
        id: 'paired-passage-extended-response-argumentative',
        displayName: 'Paired Passage Extended Response - Argumentative',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'paired-passage-extended-response-argumentative',
            displayName: 'Paired Passage Extended Response - Argumentative',
            lessonId:
              tmap['Paired Passage Extended Constructed Response'].variants[
                'AZ AASA ELA Argumentative Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-paired-passage-extended-response-argumentative',
            displayName: 'Paired Passage Extended Response - Argumentative',
            lessonId:
              tmap['Use My Materials - Paired Passage Extended Constructed Response']
                .variants['AZ AASA ELA Argumentative Extended Response'],
          },
        ],
      },
    ],
  },
  // ARIZONA AASA Math
  {
    id: 'Arizona AASA Math',
    displayName: 'Math',
    testGroup: { value: 'arizona_aasa', displayName: 'AASA' },
    states: [{ displayName: 'Arizona', value: 'AZ' }],
    questionTypes: [
      {
        id: '1-point-constructed-response',
        displayName: '1-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: false,
            variant: '1-point-constructed-response',
            displayName: '1-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants['AZ ASSA Math 1-Point'],
          },
        ],
      },
      {
        id: '2-point-constructed-response',
        displayName: '2-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '2-point-constructed-response',
            displayName: '2-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants[
                'AZ AASA Math 2-Point 2-Part'
              ],
          },
        ],
      },
    ],
  },
  // NORTH CAROLINA STATE TESTS
  {
    id: 'NC_STATE_TEST',
    displayName: 'ELA',
    testGroup: { value: 'NCPAT', displayName: 'NCPAT' },
    states: [{ displayName: 'North Carolina', value: 'NC' }],
    questionTypes: [
      {
        id: 'short_constructed_response',
        displayName: 'Reading Short Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: false,
            variant: 'reading_short_constructed_response',
            displayName: 'Reading Short Constructed Response',
            lessonId:
              tmap['Short Constructed Response'].variants[
                'NC NCPAT ELA Reading Short Constructed Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-reading_short_constructed_response',
            displayName: 'Reading Short Constructed Response',
            lessonId:
              tmap['Use My Materials - Short Constructed Response'].variants[
                'NC NCPAT ELA Reading Short Constructed Response Curify'
              ],
          },
        ],
      },
    ],
  },
  // NCPAT Math
  {
    id: 'NC_STATE_TEST_MATH',
    displayName: 'Math',
    testGroup: { value: 'NCPAT', displayName: 'NCPAT' },
    states: [{ displayName: 'North Carolina', value: 'NC' }],
    questionTypes: [
      {
        id: '1-point-constructed-response',
        displayName: '1-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: false,
            variant: '1-point-constructed-response',
            displayName: '1-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants['NC EOG Math 1-Point'],
          },
        ],
      },
    ],
  },
  // NYSTP ELA
  {
    id: 'NYSTP',
    displayName: 'ELA',
    testGroup: { value: 'NYSTP', displayName: 'NYSTP' },
    states: [{ displayName: 'New York', value: 'NY' }],
    questionTypes: [
      {
        id: '2-credit-constructed-response-question',
        displayName: '2-Credit Constructed Response Question (CRQ)',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: false,
            variant: '2-credit-constructed-response-question',
            displayName: '2-Credit Constructed Response Question (CRQ)',
            lessonId:
              tmap['Short Constructed Response'].variants[
                'NY NYSTEP ELA 2-Credit Constructed Response Question'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-2-credit-constructed-response-question',
            displayName: '2-Credit Constructed Response Question (CRQ)',
            lessonId:
              tmap['Use My Materials - Short Constructed Response'].variants[
                'NY NYSTEP ELA 2-Credit Constructed Response Question Curify'
              ],
          },
        ],
      },
      {
        id: 'paired-passage-2-credit-constructed-response-question',
        displayName: 'Paired Passage 2-Credit CRQ',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'paired-passage-2-credit-constructed-response-question',
            displayName: 'Paired Passage 2-Credit CRQ',
            lessonId:
              tmap['Paired Passage Short Constructed Response'].variants[
                'NY NYSTEP ELA 2-Credit Constructed Response Paired Passages'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-paired-passage-2-credit-constructed-response-question',
            displayName: 'Paired Passage 2-Credit CRQ',
            lessonId:
              tmap['Use My Materials - Paired Passage Short Constructed Response']
                .variants[
                'NY NYSTEP ELA 2-Credit Constructed Response Paired Passage Curify'
              ],
          },
        ],
      },
      {
        id: '4-credit-constructed-response-question',
        displayName: '4-Credit Constructed Response Question (CRQ)',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '4-credit-constructed-response-question',
            displayName: '4-Credit Constructed Response Question (CRQ)',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'NY NYSTP ELA 4-Point Constructed Response Question'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-4-credit-constructed-response-question',
            displayName: '4-Credit Constructed Response Question (CRQ)',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'NY NYSTP ELA 4-Point Constructed Response Curify'
              ],
          },
        ],
      },
      {
        id: 'paired-passage-4-credit-constructed-response-question',
        displayName: 'Paired Passage 4-Credit CRQ',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'paired-passage-4-credit-constructed-response-question',
            displayName: 'Paired Passage 4-Credit CRQ',
            lessonId:
              tmap['Paired Passage Extended Constructed Response'].variants[
                'NY NYSTP ELA 4-Point Constructed Response Question'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-paired-passage-4-credit-constructed-response-question',
            displayName: 'Paired Passage 4-Credit CRQ',
            lessonId:
              tmap['Use My Materials - Paired Passage Extended Constructed Response']
                .variants['NY NYSTP ELA 4-Point Constructed Response Question'],
          },
        ],
      },
    ],
  },
  // NY Regents
  {
    id: 'NY Regents',
    displayName: 'ELA',
    testGroup: { value: 'NY Regents', displayName: 'Regents' },
    states: [{ displayName: 'New York', value: 'NY' }],
    questionTypes: [
      {
        id: 'regents-text-analysis-response',
        displayName: 'Text Analysis Response',
        grades: [9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: false,
            variant: 'regents-text-analysis-response',
            displayName: 'Text Analysis Response',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'NY NYSTP ELA Text-Analysis Question'
              ],
          },
          {
            isPremium: false,
            variant: 'upload-regents-text-analysis-response',
            displayName: 'Text Analysis Response',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'NY NYSTP ELA Text-Analysis Question Curify'
              ],
          },
        ],
      },
      {
        id: 'regents-writing-from-sources-argument-essay',
        displayName: 'Writing from Sources Argument Essay',
        grades: [9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'regents-writing-from-sources-argument-essay',
            displayName: 'Argument Essay',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'NY NYSTP ELA Writing from Sources Argument Essay'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-regents-writing-from-sources-argument-essay',
            displayName: 'Writing from Sources Argument Essay',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'NY NYSTP ELA Writing from Sources Argument Essay Curify'
              ],
          },
        ],
      },
      {
        id: 'paired-passage-regents-writing-from-sources-argument-essay',
        displayName: 'Paired Passage Writing from Sources Argument Essay',
        grades: [9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'paired-passage-regents-writing-from-sources-argument-essay',
            displayName: 'Paired Passage Writing from Sources Argument Essay',
            lessonId:
              tmap['Paired Passage Extended Constructed Response'].variants[
                'NY NYSTP ELA Writing from Sources Argument Essay'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-paired-passage-regents-writing-from-sources-argument-essay',
            displayName: 'Paired Passage Writing from Sources Argument Essay',
            lessonId:
              tmap['Use My Materials - Paired Passage Extended Constructed Response']
                .variants['NY NYSTP ELA Writing from Sources Argument Essay'],
          },
        ],
      },
    ],
  },
  // NYSTP Math
  {
    id: 'NYSTPMath',
    displayName: 'Math',
    testGroup: { value: 'NYSTP', displayName: 'NYSTP' },
    states: [{ displayName: 'New York', value: 'NY' }],
    questionTypes: [
      {
        id: '1-point-text-constructed-response',
        displayName: '1-Point Text Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: false,
            variant: '1-point-text-constructed-response',
            displayName: '1-Point Text Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants[
                'NY NYSTP Math 1-Point'
              ],
          },
        ],
      },
      {
        id: '2-point-text-constructed-response',
        displayName: '2-Point Text Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '2-point-text-constructed-response',
            displayName: '2-Point Text Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants[
                'NY NYSTP Math 2-Point'
              ],
          },
        ],
      },
      {
        id: '3-point-text-constructed-response',
        displayName: '3-Point Text Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '3-point-text-constructed-response',
            displayName: '3-Point Text Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants['NY NYSTP 3-Point'],
          },
        ],
      },
      {
        id: '2-point-whiteboard-constructed-response',
        displayName: '2-Point Whiteboard Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '2-point-whiteboard-constructed-response',
            displayName: '2-Point Whiteboard Constructed Response',
            lessonId: config.testprep.nystepMath2PointWhiteboardConstructedResponse,
          },
        ],
      },
      {
        id: '3-point-whiteboard-constructed-response',
        displayName: '3-Point Whiteboard Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '3-point-whiteboard-constructed-response',
            displayName: '3-Point Whiteboard Constructed Response',
            lessonId: config.testprep.nystepMath3PointWhiteboardConstructedResponse,
          },
        ],
      },
    ],
  },
  // FL BEST
  {
    id: 'FL_BEST',
    displayName: 'ELA',
    testGroup: { value: 'FL_BEST', displayName: 'BEST' },
    states: [{ displayName: 'Florida', value: 'FL' }],
    questionTypes: [
      {
        id: 'ECR - Expository',
        displayName: 'Extended Response - Expository',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: false,
            variant: 'expository-extended-response',
            displayName: 'Expository Extended Response',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'FL BEST ELA Expository Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-expository-extended-response',
            displayName: 'Expository Extended Response',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'FL BEST ELA Expository Extended Response Curify'
              ],
          },
        ],
      },
      {
        id: 'ECR - Argumentation',
        displayName: 'Extended Response - Argumentation',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'argumentation-extended-response',
            displayName: 'Argumentation Extended Response',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'EL BEST ELA Argumentation Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-argumentation-extended-response',
            displayName: 'Argumentation Extended Response',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'FL BEST ELA Argumentation Extended Response Curify'
              ],
          },
        ],
      },
    ],
  },
  // FL FAST
  {
    id: 'FL_FAST',
    displayName: 'Math',
    testGroup: { value: 'FL_FAST', displayName: 'FAST' },
    states: [{ displayName: 'Florida', value: 'FL' }],
    questionTypes: [
      {
        id: '1-point-constructed-response',
        displayName: '1-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: false,
            variant: '1-point-constructed-response',
            displayName: '1-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants['FL FAST Math 1-Point'],
          },
        ],
      },
      {
        id: '2-point-constructed-response',
        displayName: '2-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '2-point-constructed-response',
            displayName: '2-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants[
                'FL FAST Math 2-Point 2-Part'
              ],
          },
        ],
      },
    ],
  },
  // GEORGIA GMAS ELA
  {
    id: 'GMAS ELA',
    displayName: 'ELA',
    testGroup: { value: 'GMAS', displayName: 'Milestones' },
    states: [{ displayName: 'Georgia', value: 'GA' }],
    questionTypes: [
      {
        id: 'extended-writing-task',
        displayName: 'Extended Writing Task',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: false,
            variant: 'extended-writing-task',
            displayName: 'Extended Writing Task',
            lessonId:
              tmap['Writing Performance Task'].variants[
                'GA GMAS ELA Extended Writing Task'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-extended-writing-task',
            displayName: 'Extended Writing Task',
            lessonId:
              tmap['Use My Materials - Writing Performance Task'].variants[
                'GA GMAS ELA Extended Writing Task Curify'
              ],
          },
        ],
      },
    ],
  },
  // GEORGIA GMAS Math
  {
    id: 'GMAS Math',
    displayName: 'Math',
    testGroup: { value: 'GMAS', displayName: 'Milestones' },
    states: [{ displayName: 'Georgia', value: 'GA' }],
    questionTypes: [
      {
        id: '1-point-constructed-response',
        displayName: '1-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: false,
            variant: '1-point-constructed-response',
            displayName: '1-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants['GA GMAS Math 1-Point'],
          },
        ],
      },
      {
        id: '2-point-constructed-response',
        displayName: '2-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '2-point-constructed-response',
            displayName: '2-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants[
                'GA GMAS Math 2-Point 2-Part'
              ],
          },
        ],
      },
    ],
  },
  // OHIO
  {
    id: 'Ohio State Tests - ELA',
    displayName: 'ELA',
    testGroup: { value: 'Ohio State Tests', displayName: 'Ohio State Tests' },
    states: [{ displayName: 'Ohio', value: 'OH' }],
    questionTypes: [
      {
        id: 'extended-response-informative',
        displayName: 'Extended Response - Informative',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: false,
            variant: 'extended-response-informative',
            displayName: 'Informative Response',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'OH OST ELA Informative Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-extended-response-informative',
            displayName: 'Informative Response',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'OH OST ELA Informative Extended Response Curify'
              ],
          },
        ],
      },
      {
        id: 'extended-response-opinion',
        displayName: 'Extended Response - Opinion',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'extended-response-opinion',
            displayName: 'Opinion Response',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'OH OST ELA Opinion Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-extended-response-opinion',
            displayName: 'Opinion Response',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'OH OST ELA Opinion Extended Response Curify'
              ],
          },
        ],
      },
      {
        id: 'extended-response-argumentation',
        displayName: 'Extended Response - Argumentation',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'extended-response-argumentation',
            displayName: 'Argumentation Response',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'OH OST ELA Argumentation Extended Response'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-extended-response-argumentation',
            displayName: 'Argumentation Response',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'OH OST ELA Argumentation Extended Response Curify'
              ],
          },
        ],
      },
    ],
  },
  // Ohio State Tests - Math
  {
    id: 'Ohio State Tests - Math',
    displayName: 'Math',
    testGroup: { value: 'Ohio State Tests', displayName: 'Ohio State Tests' },
    states: [{ displayName: 'Ohio', value: 'OH' }],
    questionTypes: [
      {
        id: '1-point-constructed-response',
        displayName: '1-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: false,
            variant: '1-point-constructed-response',
            displayName: '1-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants['OH OST Math 1-Point'],
          },
        ],
      },
    ],
  },
  // ALABAMA ACAP ELA
  {
    id: 'ACAP_ELA',
    displayName: 'ELA',
    testGroup: { value: 'ACAP', displayName: 'ACAP' },
    states: [{ displayName: 'Alabama', value: 'AL' }],
    questionTypes: [
      {
        id: 'text-dependent-writing-informative-explanatory',
        displayName: 'Text-Dependent Writing - Informative/Explanatory',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: false,
            variant: 'text-dependent-writing',
            displayName: 'Text-Dependent Writing',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'AL ACAP ELA Informative Explanatory Text-Dependent Writing'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-text-dependent-writing',
            displayName: 'Text-Dependent Writing',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'AL ACAP ELA Informative Explanatory Text-Dependent Writing'
              ],
          },
        ],
      },
      {
        id: 'argumentative-text-dependent-writing',
        displayName: 'Text-Dependent Writing - Argumentative',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'argumentative-text-dependent-writing',
            displayName: 'Text-Dependent Writing - Argumentative',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'AL ACAP ELA Argumentative Text-Dependent Writing'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-argumentative-text-dependent-writing',
            displayName: 'Text-Dependent Writing - Argumentative',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'AL ACAP ELA Argumentative Text-Dependent Writing'
              ],
          },
        ],
      },
      {
        id: 'text-dependent-writing-narrative',
        displayName: 'Text-Dependent Writing - Narrative',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'text-dependent-writing-narrative',
            displayName: 'Text-Dependent Writing - Narrative',
            lessonId:
              tmap['Extended Constructed Response'].variants[
                'AL ACAP ELA Narrative Text-Dependent Writing'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-text-dependent-writing-narrative',
            displayName: 'Text-Dependent Writing - Narrative',
            lessonId:
              tmap['Use My Materials - Extended Constructed Response'].variants[
                'AL ACAP ELA Narrative Text-Dependent Writing'
              ],
          },
        ],
      },
    ],
  },
  // ALABAMA ACAP Math
  {
    id: 'ACAP_MATH',
    displayName: 'Math',
    testGroup: { value: 'ACAP', displayName: 'ACAP' },
    states: [{ displayName: 'Alabama', value: 'AL' }],
    questionTypes: [
      {
        id: 'short-answer',
        displayName: 'Short Answer',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: false,
            variant: 'short-answer',
            displayName: 'Short Answer',
            lessonId:
              tmap['Math Constructed Response Question'].variants['AL ACAP Math 1-Point'],
          },
        ],
      },
    ],
  },
  // non-state-specific tests always come last, with SBAC coming first amongst these options
  // SBAC ELA
  {
    id: 'SBAC_ELA',
    displayName: 'ELA',
    testGroup: { value: 'SBAC', displayName: 'SBAC' },
    states: [
      { displayName: 'Connecticut', value: 'CT' },
      { displayName: 'Delaware', value: 'DE' },
      { displayName: 'Hawaii', value: 'HI' },
      { displayName: 'Idaho', value: 'ID' },
      { displayName: 'Maine', value: 'ME' },
      { displayName: 'Montana', value: 'MT' },
      { displayName: 'Nevada', value: 'NV' },
      { displayName: 'Oregon', value: 'OR' },
      { displayName: 'South Dakota', value: 'SD' },
      { displayName: 'Vermont', value: 'VT' },
      { displayName: 'Washington', value: 'WA' },
    ],
    questionTypes: [
      {
        id: 'SA',
        displayName: 'Short Answer',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: false,
            variant: 'informational', // maybe change this? idk
            displayName: 'Short Answer',
            lessonId:
              tmap['Short Constructed Response'].variants['SBAC ELA Short Answer'],
          },
          {
            isPremium: true,
            variant: 'upload',
            displayName: 'Upload',
            lessonId:
              tmap['Use My Materials - Short Constructed Response'].variants[
                'SBAC ELA Short Answer Curify'
              ],
          },
        ],
      },
      {
        id: 'PP',
        displayName: 'Paired Passage Short Answer',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'paired-passage',
            displayName: 'Paired Passage Short Answer',
            lessonId:
              tmap['Paired Passage Short Constructed Response'].variants[
                'SBAC ELA Short Answer Paired Passages'
              ],
          },
          {
            isPremium: true,
            variant: 'paired-passage-upload',
            displayName: 'Paired Passage Short Answer',
            lessonId:
              tmap['Use My Materials - Paired Passage Short Constructed Response']
                .variants['SBAC ELA Short Answer Paired Passage Curify'],
          },
        ],
      },
      {
        id: 'PT - Informational',
        displayName: 'Performance Task - Informational',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'informational-composition',
            displayName: 'Informational Composition',
            lessonId:
              tmap['Writing Performance Task'].variants[
                'SBAC ELA Informational Performance Task'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-informational-composition',
            displayName: 'Informational Composition',
            lessonId:
              tmap['Use My Materials - Writing Performance Task'].variants[
                'SBAC ELA Informational Performance Task Curify'
              ],
          },
        ],
      },
      {
        id: 'PT - Opinion',
        displayName: 'Performance Task - Opinion',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'opinion-composition',
            displayName: 'Opinion Composition',
            lessonId:
              tmap['Writing Performance Task'].variants[
                'SBAC ELA Opinion Performance Task'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-opinion-composition',
            displayName: 'Opinion Composition',
            lessonId:
              tmap['Use My Materials - Writing Performance Task'].variants[
                'SBAC ELA Opinion Performance Task Curify'
              ],
          },
        ],
      },
      {
        id: 'PT - Argumentative',
        displayName: 'Performance Task - Argumentative',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'argumentative-composition',
            displayName: 'Argumentative Composition',
            lessonId:
              tmap['Writing Performance Task'].variants[
                'SBAC ELA Argumentative Performance Task'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-argumentative-composition',
            displayName: 'Argumentative Composition',
            lessonId:
              tmap['Use My Materials - Writing Performance Task'].variants[
                'SBAC ELA Argumentative Performance Task Curify'
              ],
          },
        ],
      },
      {
        id: 'PT - Narrative',
        displayName: 'Performance Task - Narrative',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'narrative-composition',
            displayName: 'Narrative Composition',
            lessonId:
              tmap['Writing Performance Task'].variants[
                'SBAC ELA Narrative Performance Task'
              ],
          },
          {
            isPremium: true,
            variant: 'upload-narrative-composition',
            displayName: 'Narrative Composition',
            lessonId:
              tmap['Use My Materials - Writing Performance Task'].variants[
                'SBAC ELA Narrative Performance Task Curify'
              ],
          },
        ],
      },
    ],
  },
  // SBAC Math
  {
    id: 'SBAC_MATH',
    displayName: 'Math',
    testGroup: { value: 'SBAC', displayName: 'SBAC' },
    states: [
      { displayName: 'Connecticut', value: 'CT' },
      { displayName: 'Delaware', value: 'DE' },
      { displayName: 'Hawaii', value: 'HI' },
      { displayName: 'Idaho', value: 'ID' },
      { displayName: 'Maine', value: 'ME' },
      { displayName: 'Montana', value: 'MT' },
      { displayName: 'Nevada', value: 'NV' },
      { displayName: 'Oregon', value: 'OR' },
      { displayName: 'South Dakota', value: 'SD' },
      { displayName: 'Vermont', value: 'VT' },
      { displayName: 'Washington', value: 'WA' },
    ],
    questionTypes: [
      {
        id: '1-point-constructed-response',
        displayName: '1-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: false,
            variant: '1-point-constructed-response',
            displayName: '1-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants[
                'Multi SBAC Math 1-Point'
              ],
          },
        ],
      },
      {
        id: '2-point-constructed-response',
        displayName: '2-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '2-point-constructed-response',
            displayName: '2-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants[
                'Multi SBAC Math 2-Point'
              ],
          },
        ],
      },
      {
        id: '3-point-constructed-response',
        displayName: '3-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '3-point-constructed-response',
            displayName: '3-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants[
                'Multi SBAC Math 3-Point'
              ],
          },
        ],
      },
      {
        id: '4-point-constructed-response',
        displayName: '4-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '4-point-constructed-response',
            displayName: '4-Point Constructed Response',
            lessonId:
              tmap['Math Constructed Response Question'].variants[
                'Multi SBAC Math 4-Point'
              ],
          },
        ],
      },
      {
        id: '1-point-performance-task-component',
        displayName: '1-Point Performance Task Component',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '1-point-performance-task-component',
            displayName: '1-Point Performance Task Component',
            lessonId:
              tmap['Performance Task Component'].variants[
                'Multi SBAC Math Performance Task 1-Point'
              ],
          },
        ],
      },
      {
        id: '2-point-performance-task-component',
        displayName: '2-Point Performance Task Component',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '2-point-performance-task-component',
            displayName: '2-Point Performance Task Component',
            lessonId:
              tmap['Performance Task Component'].variants[
                'Multi SBAC Math Performance Task 2-Point'
              ],
          },
        ],
      },
      {
        id: '3-point-performance-task-component',
        displayName: '3-Point Performance Task Component',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '3-point-performance-task-component',
            displayName: '3-Point Performance Task Component',
            lessonId:
              tmap['Performance Task Component'].variants[
                'Multi SBAC Performance Task 3-Point'
              ],
          },
        ],
      },
      {
        id: '4-point-performance-task-component',
        displayName: '4-Point Performance Task Component',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: '4-point-performance-task-component',
            displayName: '4-Point Performance Task Component',
            lessonId:
              tmap['Performance Task Component'].variants[
                'Multi SBAC Performance Task 4-Point'
              ],
          },
        ],
      },
      {
        id: 'performance-task-simulation',
        displayName: 'Performance Task Simulation',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            isPremium: true,
            variant: 'performance-task-simulation',
            displayName: 'Performance Task Simulation',
            lessonId:
              tmap['Math Performance Task Simulation'].variants[
                'Multi SBAC Math Performance Task'
              ],
          },
        ],
      },
    ],
  },
  // WIDA ACCESS ELLs
  {
    id: 'WIDA',
    displayName: 'EL Proficiency',
    testGroup: { value: 'WIDA', displayName: 'WIDA ACCESS' },
    states: [
      { displayName: 'Alabama', value: 'AL' },
      { displayName: 'Alaska', value: 'AK' },
      { displayName: 'Colorado', value: 'CO' },
      { displayName: 'Connecticut', value: 'CT' },
      { displayName: 'Delaware', value: 'DE' },
      { displayName: 'District of Columbia', value: 'DC' },
      { displayName: 'Florida', value: 'FL' },
      { displayName: 'Georgia', value: 'GA' },
      { displayName: 'Hawaii', value: 'HI' },
      { displayName: 'Idaho', value: 'ID' },
      { displayName: 'Illinois', value: 'IL' },
      { displayName: 'Indiana', value: 'IN' },
      { displayName: 'Kentucky', value: 'KY' },
      { displayName: 'Maine', value: 'ME' },
      { displayName: 'Maryland', value: 'MD' },
      { displayName: 'Massachusetts', value: 'MA' },
      { displayName: 'Michigan', value: 'MI' },
      { displayName: 'Minnesota', value: 'MN' },
      { displayName: 'Mississippi', value: 'MS' },
      { displayName: 'Missouri', value: 'MO' },
      { displayName: 'Montana', value: 'MT' },
      { displayName: 'Nevada', value: 'NV' },
      { displayName: 'New Hampshire', value: 'NH' },
      { displayName: 'New Jersey', value: 'NJ' },
      { displayName: 'New Mexico', value: 'NM' },
      { displayName: 'North Carolina', value: 'NC' },
      { displayName: 'North Dakota', value: 'ND' },
      { displayName: 'Oklahoma', value: 'OK' },
      { displayName: 'Oregon', value: 'OR' },
      { displayName: 'Pennsylvania', value: 'PA' },
      { displayName: 'Rhode Island', value: 'RI' },
      { displayName: 'South Carolina', value: 'SC' },
      { displayName: 'South Dakota', value: 'SD' },
      { displayName: 'Utah', value: 'UT' },
      { displayName: 'Vermont', value: 'VT' },
      { displayName: 'Virginia', value: 'VA' },
      { displayName: 'Washington', value: 'WA' },
      { displayName: 'Wisconsin', value: 'WI' },
      { displayName: 'Wyoming', value: 'WY' },
    ],
    questionTypes: [
      {
        id: 'tier_a_write_story',
        displayName: 'Tier A: Write A Story',
        grades: ['K', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'tier_a_write_story',
            displayName: 'Tier A: Write A Story',
            lessonId: config.testprep.widaTierAWriteStory,
          },
        ],
      },
      {
        id: 'tier_a_make_a_decision',
        displayName: 'Tier A: Make a Decision',
        grades: ['K', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'tier_a_make_a_decision',
            displayName: 'Tier A: Make a Decision',
            lessonId: config.testprep.widaTierAMakeADecision,
          },
        ],
      },
      {
        id: 'tier_b_c_step_by_step_description',
        displayName: 'Tier B/C: Step-by-Step Description',
        grades: ['K', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'tier_b_c_step_by_step_description',
            displayName: 'Tier B/C: Step-by-Step Description',
            lessonId: config.testprep.widaTierBCStepByStepDescription,
          },
        ],
      },
    ],
  },
  // RACE (all states)
  {
    id: 'RACE',
    displayName: 'ELA',
    testGroup: { value: 'RACE', displayName: 'R.A.C.E.' },
    states: [
      { displayName: 'Writing Frameworks', value: 'ALL' },
      { displayName: 'Texas', value: 'TX' },
    ],
    questionTypes: [
      {
        id: 'constructed_response',
        displayName: 'Constructed Response',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.raceConstructedResponse,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.raceUpload,
          },
        ],
      },
    ],
  },
] as const;

const templatedTests: Test[] = (_templatedTests as unknown) as Test[];

export type TestGroup = typeof _templatedTests[number]['testGroup']['value'];
export type TestId = typeof _templatedTests[number]['id'];
export type QuestionTypeId = typeof _templatedTests[number]['questionTypes'][number]['id'];

export function getTestHasUploadOptions_templated(
  test: string,
  questionType: string | undefined,
): boolean {
  const testObj = templatedTests.find(t => t.id === test);
  if (!testObj) return false;
  const questionTypeObj = testObj.questionTypes.find(q => q.id === questionType);
  if (!questionTypeObj) return false;
  return questionTypeObj.lessonVariants.some(l => l.variant.includes('upload'));
}

export function useAllTestsForTestGroup(
  state: string,
  testGroupValue: string | undefined,
  generators: ConsumerGenerator[] | undefined,
  userAccessBadge: UserAccessBadge | undefined,
): DropdownOption[] {
  const testPrepTree = useTestPrepTree();
  const relevantTests = testPrepTree.filter(
    t => t.testGroup.value === testGroupValue && t.states.some(s => s.value === state),
  );

  return relevantTests.map(test => {
    const hasAtLeastOneFreeGenerator = test.questionTypes.some(qType =>
      qType.lessonVariants.some(variant => {
        if (variant.variant.includes('upload')) return false;
        if ('isPremium' in variant) {
          return !variant.isPremium;
        }

        const foundGenerator = generators?.find(g => g.id === variant.lessonId);

        const planType: GeneratorCoarseLicenseLevel = getPlanType({
          aiContent: foundGenerator?.aiContent,
          questionType: qType,
          generatorId: variant.lessonId,
        });

        return planType === GeneratorCoarseLicenseLevel.freemium;
      }),
    );

    const isEnterprise = !hasAtLeastOneFreeGenerator;

    let icon: JSX.Element | undefined;
    if (isEnterprise) {
      icon = (
        <TestprepPlanIcon
          hasAccessToGenerator={
            userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise
          }
          isEnterprisePlanGenerator={true}
          isPremiumPlanGenerator={false}
        />
      );
    }

    return {
      displayName: test.displayName,
      value: test.id,
      icon,
      isEnterprise,
    };
  });
}

export const getFirstTestGroupForState_templated = (
  state: string,
): string | undefined => {
  const test = templatedTests.find(t => t.states.some(s => s.value === state));
  return test ? test.testGroup.value : undefined;
};

export const getFirstTestForGroup_templated = (
  state: string,
  groupValue: string,
): string | undefined => {
  const test = templatedTests.find(
    t => t.states.some(s => s.value === state) && t.testGroup.value === groupValue,
  );
  return test ? test.id : undefined;
};
export function getQuestionTypesForStateAndTestGroup(
  state: string,
  groupValue: string,
): string[] {
  const test = templatedTests.find(
    t => t.states.some(s => s.value === state) && t.testGroup.value === groupValue,
  );
  return test?.questionTypes.map(q => q.id) ?? [];
}

export function useGroupedStatesWithTests(): {
  option: DropdownOption;
  subOptions: DropdownOption[];
}[] {
  const map = new Map<
    string,
    {
      option: DropdownOption;
      subOptions: DropdownOption[];
    }
  >();

  const testPrepTree = useTestPrepTree();

  testPrepTree.forEach(currTest => {
    currTest.states.forEach(stateObj => {
      if (!map.has(stateObj.value)) {
        map.set(stateObj.value, {
          option: { ...stateObj, displayDivider: true },
          subOptions: [],
        });
      }

      const existingGroup = map.get(stateObj.value);
      if (!existingGroup) return;

      // Check if testGroup already in subOptions
      const alreadyExists = existingGroup.subOptions.some(
        g => g.value === currTest.testGroup.value,
      );

      if (!alreadyExists) {
        existingGroup.subOptions.push({
          displayName: currTest.testGroup.displayName,
          value: currTest.testGroup.value,
        });
      }
    });
  });

  const groupedStates = Array.from(map.values());

  //Alphabetically sort the states except for All states which should appear at the end
  const alphabeticalGroupedStates = groupedStates.sort((a, b) => {
    if (a.option.value === 'ALL') return 1;
    if (b.option.value === 'ALL') return -1;
    return a.option.displayName.localeCompare(b.option.displayName);
  });

  return alphabeticalGroupedStates;
}

export function useGetQuestionType({
  testId,
  questionTypeId,
}: {
  testId: string | undefined;
  questionTypeId: string | undefined;
}): QuestionType | undefined {
  const testPrepTree = useTestPrepTree();
  const test = testPrepTree.find(t => t.id === testId);
  if (!test) return undefined;
  const questionType = test.questionTypes.find(q => q.id === questionTypeId);
  if (!questionType) return undefined;
  return questionType;
}

export function getQuestionTypes_templated({
  testId,
  generators,
  userAccessBadge,
}: {
  testId: string | undefined;
  generators: ConsumerGenerator[] | undefined;
  userAccessBadge: UserAccessBadge | undefined;
}): DropdownOption[] {
  const test = templatedTests.find(t => t.id === testId);
  if (!test) return [];

  return test.questionTypes.map(q => {
    // 1. Determine if there's at least one FREE (non-upload) generator.
    const hasAnyFreeGenerator = q.lessonVariants.some(variant => {
      // skip if it's an "upload" variant (if that's your logic)
      if (variant.variant.includes('upload')) return false;

      // find the corresponding generator for this variant's lessonId
      const foundGenerator = generators?.find(g => g.id === variant.lessonId);

      // figure out the plan type
      const planType = getPlanType({
        aiContent: foundGenerator?.aiContent,
        generatorId: variant.lessonId,
        questionType: q,
      });

      // If ANY variant is 'freemium', the question type is considered free
      return planType === GeneratorCoarseLicenseLevel.freemium;
    });

    // 2. Mark question type premium if no free generators found
    const isEnterprise = !hasAnyFreeGenerator;
    let icon: JSX.Element | undefined;

    // 3. If it's premium, attach a premium/enterprise icon
    if (isEnterprise) {
      icon = (
        <TestprepPlanIcon
          hasAccessToGenerator={
            userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise
          }
          isEnterprisePlanGenerator={true}
          isPremiumPlanGenerator={false}
        />
      );
    }

    // 4. Return the dropdown option
    return {
      displayName: q.displayName,
      value: q.id,
      icon,
      isEnterprise,
    };
  });
}

export function useGetLessonVariants({
  testId,
  questionTypeId,
  generators,
  userAccessBadge,
  isUpload,
}: {
  testId: string | undefined;
  questionTypeId: string | undefined;
  generators: ConsumerGenerator[] | undefined;
  userAccessBadge: UserAccessBadge | undefined;
  isUpload: boolean | undefined;
}): DropdownOption[] {
  const testPrepTree = useTestPrepTree();

  return useMemo(() => {
    const test = testPrepTree.find(t => t.id === testId);
    if (!test) return [];

    const questionType = test.questionTypes.find(q => q.id === questionTypeId);
    if (!questionType) return [];

    if (!isUpload) {
      //if it is not upload we filter out any lesson variants that are not upload
      const lessonVariants = questionType.lessonVariants;
      return lessonVariants
        .map(l => {
          if (l.variant.includes('upload')) return undefined;
          const foundGenerator = generators?.find(g => g.id === l.lessonId);
          const planType = getPlanType({
            aiContent: foundGenerator?.aiContent,
            generatorId: l.lessonId,
            questionType: questionType,
          });

          return {
            displayName: l.displayName || l.variant,
            value: l.variant,
            icon:
              planType === 'enterprise' || planType === 'premium' ? (
                <TestprepPlanIcon
                  hasAccessToGenerator={
                    userAccessBadge?.license.coarseLevel ===
                    LicenseCoarseLevels.enterprise
                  }
                  isEnterprisePlanGenerator={
                    planType === 'enterprise' || planType === 'premium'
                  }
                  isPremiumPlanGenerator={
                    planType === 'enterprise' || planType === 'premium'
                  }
                />
              ) : (
                undefined
              ),
            isEnterprise: planType === 'enterprise' || planType === 'premium',
          };
        })
        .filter(notUndefined);
    }

    //if it is upload we filter out any lesson variants that are not upload
    const lessonVariants = questionType.lessonVariants;
    return lessonVariants
      .map(l => {
        if (!l.variant.includes('upload')) return undefined;
        const foundGenerator = generators?.find(g => g.id === l.lessonId);
        const planType = getPlanType({
          aiContent: foundGenerator?.aiContent,
          generatorId: l.lessonId,
          questionType: questionType,
        });
        return {
          displayName: l.displayName || l.variant,
          value: l.variant,
          icon:
            planType === 'enterprise' || planType === 'premium' ? (
              <TestprepPlanIcon
                hasAccessToGenerator={
                  userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise
                }
                isEnterprisePlanGenerator={
                  planType === 'enterprise' || planType === 'premium'
                }
                isPremiumPlanGenerator={false}
              />
            ) : (
              undefined
            ),
        };
      })
      .filter(notUndefined);
  }, [testPrepTree, testId, questionTypeId, generators, userAccessBadge, isUpload]);
}

export function getPlanType({
  aiContent,
  questionType,
  generatorId,
}: {
  aiContent?: ConsumerGenerator['aiContent'];
  questionType: QuestionType;
  generatorId: string;
}): GeneratorCoarseLicenseLevel {
  const questionTypeVariant = questionType.lessonVariants.find(
    v => v.lessonId === generatorId,
  );
  // this only exists in the templated test prep tree thing, and only for templated generators
  if (questionTypeVariant && 'isPremium' in questionTypeVariant) {
    return questionTypeVariant.isPremium
      ? GeneratorCoarseLicenseLevel.enterprise
      : GeneratorCoarseLicenseLevel.freemium;
  }

  if (aiContent?.generatorPlan) {
    return aiContent?.generatorPlan;
  }
  if (aiContent?.isPremiumGenerator) {
    return GeneratorCoarseLicenseLevel.enterprise;
  }
  return GeneratorCoarseLicenseLevel.freemium;
}

export function useGetLessonIds({
  testId,
  questionTypeId,
  lessonVariantId,
  isUpload,
}: {
  testId?: string;
  questionTypeId?: string;
  lessonVariantId?: string;
  isUpload?: boolean;
}): Array<Readonly<string>> {
  const testPrepTree = useTestPrepTree();

  return useMemo(() => {
    const test = testPrepTree.find(t => t.id === testId);
    if (!test) return [];

    let questionTypes = test.questionTypes;
    if (questionTypeId) {
      questionTypes = questionTypes.filter(q => q.id === questionTypeId);
    }

    let lessonVariants = questionTypes.flatMap(q => q.lessonVariants);

    if (lessonVariantId) {
      lessonVariants = lessonVariants.filter(v => v.variant === lessonVariantId);
    }

    if (isUpload !== undefined) {
      if (isUpload) {
        lessonVariants = lessonVariants.filter(v => v.variant.includes('upload'));
      } else {
        lessonVariants = lessonVariants.filter(v => !v.variant.includes('upload'));
      }
    }
    return [...new Set(lessonVariants.map(v => v.lessonId).filter(Boolean))];
  }, [testPrepTree, testId, questionTypeId, lessonVariantId, isUpload]);
}
